import { useCallback, useEffect, useMemo, useRef } from "react";
import type { KeyboardCoordinateGetter } from "@dnd-kit/core";
import { createAtom } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import type { GridController } from "../../focus/gridController";
import { flashDroppedRow } from "../../motion/flashDroppedRow";
import type { DataGridInstance } from "../../types";
import { useConstant } from "@hooks/useConstant";
import type { DragStartEvent, RowDragContextValue } from "./dragContext";
import type { DraggableApi, DragKindController } from "./dragKind";
import {
    describePosition,
    describeTargetStep,
    getRowIndicator,
    getRowLabel,
    isSameTarget,
    type RowIndicatorState,
} from "./rowDrag";
import { createRowDragSession, type RowDragSession } from "./rowDragSession";
import { type RowDropTarget, stepKeyboardTarget } from "./rowDropTarget";

const ARROW_STEPS: Partial<Record<string, 1 | -1>> = {
    ArrowDown: 1,
    ArrowUp: -1,
};

/**
 * Row drag and drop: one draggable for every row (the pressed handle
 * becomes its node), targets computed from the virtualizer's geometry, and
 * a keyboard mode stepping through every distinct landing spot. Rows never
 * re-render while dragging; only the indicator and the source row do.
 */
export const useRowDragController = <TData extends RowData>({
    instance,
    controller,
    announce,
    instructionsId,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    announce: (message: string) => void;
    instructionsId: string;
}): DragKindController<RowIndicatorState> & {
    contextValue: RowDragContextValue;
    coordinateGetter: KeyboardCoordinateGetter;
} => {
    const { table, optionsRef, isTree } = instance;
    const sourceRef = useRef<DraggableApi | null>(null);
    const pendingRowIdRef = useRef<string | null>(null);
    const sessionRef = useRef<RowDragSession | null>(null);
    const indicator = useConstant(() =>
        createAtom<RowIndicatorState | null>(null),
    );
    // A table that unmounts mid-drag drops its listeners.
    useEffect(() => () => sessionRef.current?.cleanup(), []);
    const { sorting, grouping } = table.state;
    const isBlocked = sorting.length > 0 || grouping.length > 0;

    const labelOf = useCallback(
        (rowId: string) =>
            getRowLabel({
                row: table.getRow(rowId, true),
                getLabel: optionsRef.current.rowReorder?.getRowLabel,
            }),
        [optionsRef, table],
    );

    const setTarget = useCallback(
        (session: RowDragSession, target: RowDropTarget | null) => {
            if (isSameTarget(session.target, target)) {
                return false;
            }
            session.target = target;
            indicator.set(
                target &&
                    getRowIndicator({
                        controller,
                        target,
                        indent: session.indent,
                    }),
            );
            return true;
        },
        [controller, indicator],
    );

    const startRowDrag = useCallback(
        (event: DragStartEvent, rowId: string) => {
            const source = sourceRef.current;
            if (isBlocked) {
                if (
                    "key" in event &&
                    (event.key === " " || event.key === "Enter")
                ) {
                    announce("Clear sorting and grouping to reorder rows.");
                }
                return;
            }
            if (!source) {
                return;
            }
            pendingRowIdRef.current = rowId;
            source.setNodeRef(event.currentTarget);
            source.setActivatorNodeRef(event.currentTarget);
            const listener =
                event.type === "pointerdown"
                    ? source.listeners?.onPointerDown
                    : source.listeners?.onKeyDown;
            listener?.(event);
        },
        [announce, isBlocked],
    );

    const finish = useCallback(() => {
        const session = sessionRef.current;
        session?.cleanup();
        sessionRef.current = null;
        pendingRowIdRef.current = null;
        indicator.set(null);
        controller.dragSource.set(null);
        return session;
    }, [controller, indicator]);

    const onStart = useCallback(
        (activatorEvent: Event | null) => {
            const rowId = pendingRowIdRef.current;
            if (rowId === null) {
                return false;
            }
            const session = createRowDragSession({
                table,
                controller,
                rowId,
                label: labelOf(rowId),
                isTree,
                canDrop: optionsRef.current.rowReorder?.canDrop,
                activatorEvent,
                setTarget,
            });
            sessionRef.current = session;
            controller.dragSource.set({ kind: "row", id: rowId });
            announce(
                session.input === "keyboard"
                    ? `Picked up ${session.label}. Use the up and down arrow keys to move it.`
                    : `Picked up ${session.label}.`,
            );
            return { label: session.label, input: session.input };
        },
        [announce, controller, isTree, labelOf, optionsRef, setTarget, table],
    );

    const onEnd = useCallback(() => {
        const session = finish();
        if (!session) {
            return;
        }
        const { target, label, rowId } = session;
        if (!target) {
            announce(`${label} stayed in place.`);
            return;
        }
        // A row dropped into a collapsed row stays visible.
        if (target.position === "into") {
            const { targetRowId } = target;
            table.setExpanded((expanded) =>
                expanded === true
                    ? expanded
                    : { ...expanded, [targetRowId]: true },
            );
        }
        optionsRef.current.rowReorder?.onRowMove({
            rowId,
            targetRowId: target.targetRowId,
            position: target.position,
        });
        if (instance.motion.animateRowOrder && session.input === "pointer") {
            controller.rowMotion.requestReorder(rowId);
        }
        flashDroppedRow({ controller, rowId, input: session.input });
        announce(
            `Moved ${label} ${describePosition({
                position: target.position,
                targetLabel: labelOf(target.targetRowId),
            })}.`,
        );
    }, [
        announce,
        controller,
        finish,
        instance.motion.animateRowOrder,
        labelOf,
        optionsRef,
        table,
    ]);

    const onCancel = useCallback(() => {
        const session = finish();
        if (session) {
            announce(`Canceled moving ${session.label}.`);
        }
    }, [announce, finish]);

    const coordinateGetter = useCallback<KeyboardCoordinateGetter>(
        (event, { currentCoordinates }) => {
            const session = sessionRef.current;
            const direction = ARROW_STEPS[event.code];
            if (!session || direction === undefined) {
                return undefined;
            }
            event.preventDefault();
            const next = stepKeyboardTarget({
                targets: session.keyboardTargets,
                current: session.target,
                sourceIndex: session.snapshot.sourceIndex,
                direction,
            });
            if (next) {
                controller.scrollToRowIndex(next.index);
            }
            if (setTarget(session, next) && next) {
                announce(
                    describeTargetStep({
                        position: next.position,
                        targetLabel: labelOf(next.targetRowId),
                    }),
                );
            }
            // The preview stays put; the indicator shows the target.
            return currentCoordinates;
        },
        [announce, controller, labelOf, setTarget],
    );

    const contextValue = useMemo(
        (): RowDragContextValue => ({
            startRowDrag,
            isBlocked,
            instructionsId,
        }),
        [instructionsId, isBlocked, startRowDrag],
    );

    return {
        sourceRef,
        indicator,
        contextValue,
        coordinateGetter,
        onStart,
        onEnd,
        onCancel,
    };
};
